import type { FC, ReactNode } from 'react'
import type { CategoryValue } from '@/types'

import { useSettings } from '@/components/generals/settings-provider'
import { pluralize } from '@/lib/format'

interface CategoryValueChartProps {
    categories: CategoryValue[]
}

const MAX_ROWS = 6

const CategoryValueChart: FC<CategoryValueChartProps> = ({ categories }): ReactNode => {
    const { money, number } = useSettings()
    const withValue = categories.filter(category => category.productCount > 0)
    const total = withValue.reduce((sum, category) => sum + category.value, 0)

    if (!withValue.length || total <= 0) {
        return (
            <p className='text-sm text-muted-foreground'>
                Asigna un precio de compra a tus productos para ver cómo se reparte el valor del inventario.
            </p>
        )
    }

    const rows = withValue.slice(0, MAX_ROWS)
    const rest = withValue.slice(MAX_ROWS)
    const restValue = rest.reduce((sum, category) => sum + category.value, 0)
    const restCount = rest.reduce((sum, category) => sum + category.productCount, 0)
    const max = Math.max(...rows.map(category => category.value), restValue, 1)

    return (
        <div className='flex flex-col gap-3.5'>
            <ul className='flex flex-col gap-3.5'>
                {rows.map(category => (
                    <li
                        key={category.id ?? 'sin-categoria'}
                        title={`${category.name}: ${money(category.value)} en ${number(category.productCount)} ${pluralize(category.productCount, 'producto', 'productos')}`}
                    >
                        <div className='flex items-center justify-between gap-3 text-sm'>
                            <span className='flex min-w-0 items-center gap-2'>
                                <span
                                    className='size-2 shrink-0 rounded-full'
                                    style={{ backgroundColor: category.color ?? 'var(--muted-foreground)' }}
                                    aria-hidden='true'
                                />

                                {category.id ? (
                                    <span className='truncate' data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>
                                ) : (
                                    <span className='truncate text-muted-foreground'>{category.name}</span>
                                )}
                            </span>

                            <span className='shrink-0 font-medium tabular-nums'>
                                {money(category.value)}
                            </span>
                        </div>

                        <div className='mt-1.5 h-2 w-full rounded-full bg-muted' aria-hidden='true'>
                            <div
                                className='h-full rounded-l-full rounded-r-[4px] bg-primary transition-[width] duration-500'
                                style={{ width: `${category.value > 0 ? Math.max(2, (category.value / max) * 100) : 0}%` }}
                            />
                        </div>
                    </li>
                ))}

                {rest.length > 0 && (
                    <li title={`Otras categorías: ${money(restValue)} en ${number(restCount)} productos`}>
                        <div className='flex items-center justify-between gap-3 text-sm'>
                            <span className='text-muted-foreground'>
                                Otras {number(rest.length)} categorías
                            </span>

                            <span className='shrink-0 font-medium tabular-nums'>
                                {money(restValue)}
                            </span>
                        </div>

                        <div className='mt-1.5 h-2 w-full rounded-full bg-muted' aria-hidden='true'>
                            <div
                                className='h-full rounded-l-full rounded-r-[4px] bg-primary/50'
                                style={{ width: `${Math.max(2, (restValue / max) * 100)}%` }}
                            />
                        </div>
                    </li>
                )}
            </ul>

            <p className='border-t pt-3 text-xs text-muted-foreground'>
                Total a precio de compra: <span className='font-medium text-foreground tabular-nums'>{money(total)}</span>
            </p>
        </div>
    )
}

export default CategoryValueChart
